import {
  ETaxBlockedDocument,
  ETaxCreateBatchFailedItem,
  ETaxCreateBatchResult,
  ETaxDocument,
} from "../../types/e-tax.type";
import { EXPIRED_TOOLTIP } from "./AutoSend/autoSendMeta";
import { SendSummary } from "./ConfirmSendModal";

// Pure helpers behind the manual send page (ETaxSendPage). Kept free of React so the
// rules — which rows may be picked, how a gate refusal is read, how chunked results
// add up — are unit-testable on their own.

/** The API rejects more than 1000 items per send call, so a larger send is split into chunks of this size. */
export const SEND_CHUNK_SIZE = 1000;

export const INELIGIBLE_TOOLTIP =
  "บิลนี้ไม่ผ่านการตรวจสอบก่อนส่ง ส่ง E-Tax ไม่ได้ ดูเหตุผลที่ช่องสถานะ";

export const RESEND_BLOCKED_TOOLTIP =
  "KTB/กรมสรรพากรปฏิเสธเอกสารนี้แล้ว ส่งซ้ำไม่ได้ ให้ออกเอกสารแบบกระดาษแทน";

type UnsendableFields = Pick<ETaxDocument, "etax_eligible"> &
  Partial<Pick<ETaxDocument, "is_expired" | "is_resend_blocked">>;

/**
 * Why this row cannot be sent by hand, or null when it can. A non-null answer means the
 * checkbox is disabled and the row never joins "ส่งทั้งหมด": createBatch would refuse the
 * whole request for it. Expired wins because it is final; a resend block is next.
 */
export const getUnsendableReason = (doc: UnsendableFields): string | null => {
  if (doc.is_expired === true) return EXPIRED_TOOLTIP;
  if (doc.is_resend_blocked === true) return RESEND_BLOCKED_TOOLTIP;
  if (doc.etax_eligible === false) return INELIGIBLE_TOOLTIP;
  return null;
};

export const isUnsendable = (doc: UnsendableFields): boolean =>
  getUnsendableReason(doc) !== null;

type SummarizedDocument = Pick<
  ETaxDocument,
  "net_total" | "amount_before_vat" | "vat_amount" | "document_type"
>;

/** The totals and per-type counts the confirmation dialog and the footer show for a set of documents. */
export const buildSendSummary = (docs: SummarizedDocument[]): SendSummary => {
  const typeCounts = { INV: 0, DN: 0, CN: 0 };
  let netTotal = 0;
  let amountBeforeVat = 0;
  let vatAmount = 0;
  docs.forEach((doc) => {
    netTotal += doc.net_total;
    amountBeforeVat += doc.amount_before_vat;
    vatAmount += doc.vat_amount;
    typeCounts[doc.document_type] += 1;
  });
  return {
    documentCount: docs.length,
    netTotal,
    amountBeforeVat,
    vatAmount,
    typeCounts,
  };
};

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/** The last page that still has rows for `count` items (1 when the list is empty). */
export const clampPage = (page: number, count: number, limit: number): number => {
  const lastPage = Math.max(1, Math.ceil(count / Math.max(1, limit)));
  return Math.min(Math.max(1, page), lastPage);
};

const isRefusal = (value: unknown): value is ETaxBlockedDocument => {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return typeof row.transaction_uuid === "string";
};

/**
 * The gate's per-bill refusal list from a rejected POST /etax/batches, or null when the
 * error is anything else (network, 5xx, a 400 of another kind). The contract puts the
 * array in the envelope's `error`; `errors` / `data` are read too so a small change of
 * path on the backend does not silently turn the dialog back into a bare toast.
 */
export const extractBatchRefusals = (
  error: unknown,
): ETaxBlockedDocument[] | null => {
  // Duck-typed rather than axios.isAxiosError so this module stays importable under Jest.
  const axiosLike = error as
    | { isAxiosError?: boolean; response?: { status?: number; data?: unknown } }
    | null
    | undefined;
  if (!axiosLike?.isAxiosError || axiosLike.response?.status !== 400) return null;
  const body = axiosLike.response.data as Record<string, unknown> | undefined;
  if (!body || typeof body !== "object") return null;

  for (const key of ["error", "errors", "data"]) {
    const candidate = body[key];
    if (Array.isArray(candidate) && candidate.length > 0 && candidate.every(isRefusal)) {
      return candidate.map((row) => ({
        transaction_uuid: row.transaction_uuid,
        transaction_no: row.transaction_no ?? row.transaction_uuid,
        reason_codes: Array.isArray(row.reason_codes) ? row.reason_codes : [],
        messages: Array.isArray(row.messages) ? row.messages : [],
      }));
    }
  }
  return null;
};

export interface SendResultSummary {
  requested_count: number;
  sent_count: number;
  failed_count: number;
  already_sent_count: number;
  skipped_test_count: number;
  failed: ETaxCreateBatchFailedItem[];
}

const toNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

/**
 * Adds up the per-chunk results of one send. Returns null when any chunk came back without
 * a result (a backend that still answers `data: null`): a partial sum would under-report,
 * so the caller falls back to a plain success message instead.
 */
export const mergeBatchResults = (
  results: Array<ETaxCreateBatchResult | null | undefined>,
): SendResultSummary | null => {
  if (results.length === 0) return null;
  const summary: SendResultSummary = {
    requested_count: 0,
    sent_count: 0,
    failed_count: 0,
    already_sent_count: 0,
    skipped_test_count: 0,
    failed: [],
  };
  for (const result of results) {
    if (!result || typeof result !== "object") return null;
    summary.requested_count += toNumber(result.requested_count);
    summary.sent_count += toNumber(result.sent_count);
    summary.failed_count += toNumber(result.failed_count);
    summary.already_sent_count += toNumber(result.already_sent_count);
    summary.skipped_test_count += toNumber(result.skipped_test_count);
    if (Array.isArray(result.failed)) summary.failed.push(...result.failed);
  }
  return summary;
};
